/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CruiseRealAssetDeploymentCreateRequest } from '../models/CruiseRealAssetDeploymentCreateRequest';
import type { DataAssetSearchParameters } from '../models/DataAssetSearchParameters';
import type { DataAssetSearchStatisticsResponse } from '../models/DataAssetSearchStatisticsResponse';
import type { DataSetCreateRequest } from '../models/DataSetCreateRequest';
import type { DataSetDetailResponse } from '../models/DataSetDetailResponse';
import type { DataSetResponse } from '../models/DataSetResponse';
import type { DeploymentTreeResponse } from '../models/DeploymentTreeResponse';
import type { DirectoryCreateRequest } from '../models/DirectoryCreateRequest';
import type { DirectoryDataExpertUserResponse } from '../models/DirectoryDataExpertUserResponse';
import type { DirectoryInventoryGroupBy } from '../models/DirectoryInventoryGroupBy';
import type { DirectoryInventorySummaryResponse } from '../models/DirectoryInventorySummaryResponse';
import type { DirectoryPreviewRequest } from '../models/DirectoryPreviewRequest';
import type { DirectoryPreviewResponse } from '../models/DirectoryPreviewResponse';
import type { DirectoryResponse } from '../models/DirectoryResponse';
import type { DirectoryUpdateRequest } from '../models/DirectoryUpdateRequest';
import type { ManifestType } from '../models/ManifestType';
import type { RealAssetCreateRequest } from '../models/RealAssetCreateRequest';
import type { RealAssetDeploymentWithChildrenResponse } from '../models/RealAssetDeploymentWithChildrenResponse';
import type { RealAssetDeploymentWithRealAssetResponse } from '../models/RealAssetDeploymentWithRealAssetResponse';
import type { RealAssetResponse } from '../models/RealAssetResponse';
import type { RealAssetTreeResponse } from '../models/RealAssetTreeResponse';
import type { RealAssetType } from '../models/RealAssetType';
import type { RealAssetWithDeploymentsResponse } from '../models/RealAssetWithDeploymentsResponse';
import type { RelatedDeploymentResponse } from '../models/RelatedDeploymentResponse';
import type { SharedLinkResponse } from '../models/SharedLinkResponse';
import type { SQLModelPage_DataAssetTreeDirectoryResponse_ } from '../models/SQLModelPage_DataAssetTreeDirectoryResponse_';
import type { SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_ } from '../models/SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_';
import type { SQLModelPage_DataSetResponse_ } from '../models/SQLModelPage_DataSetResponse_';
import type { SQLModelPage_DirectoryInventoryListItem_ } from '../models/SQLModelPage_DirectoryInventoryListItem_';
import type { SQLModelPage_DirectoryResponse_ } from '../models/SQLModelPage_DirectoryResponse_';
import type { SQLModelPage_RealAssetDeploymentResponse_ } from '../models/SQLModelPage_RealAssetDeploymentResponse_';
import type { SQLModelPage_RealAssetResponse_ } from '../models/SQLModelPage_RealAssetResponse_';
import type { StorageTier } from '../models/StorageTier';
import type { StorageTierStatus } from '../models/StorageTierStatus';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DataAccessService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Search Data Assets
     * @returns SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_ Successful Response
     * @throws ApiError
     */
    public searchDataAssets({
        query,
        sealogEventQuery,
        exclude,
        regex = false,
        fileExtensions,
        directory,
        parentPrefix,
        includeDescendantDeployments = false,
        squashByPath = false,
        dataSetPublicIds,
        dataAssetPublicIds,
        manifestTypes,
        manifestPublicIds,
        realAssetPublicIds,
        realAssetDeploymentPublicIds,
        excludedRealAssetDeploymentPublicIds,
        storageTiers,
        storageTierStatuses,
        sealogEventValues,
        sealogEventFreeTexts,
        sealogEventOptionNames,
        sealogEventOptionValues,
        recordModifiedBefore,
        recordModifiedAfter,
        assetModifiedBefore,
        assetModifiedAfter,
        sortField = 'asset_updated_dt',
        sortOrder = 'desc',
        page = 1,
        size = 50,
    }: {
        query?: string,
        sealogEventQuery?: string,
        exclude?: string,
        regex?: boolean,
        fileExtensions?: Array<string>,
        directory?: string,
        parentPrefix?: string,
        includeDescendantDeployments?: boolean,
        squashByPath?: boolean,
        dataSetPublicIds?: Array<string>,
        dataAssetPublicIds?: Array<string>,
        manifestTypes?: Array<ManifestType>,
        manifestPublicIds?: Array<string>,
        realAssetPublicIds?: Array<string>,
        realAssetDeploymentPublicIds?: Array<string>,
        excludedRealAssetDeploymentPublicIds?: Array<string>,
        storageTiers?: Array<StorageTier>,
        storageTierStatuses?: Array<StorageTierStatus>,
        sealogEventValues?: Array<string>,
        sealogEventFreeTexts?: Array<string>,
        sealogEventOptionNames?: Array<string>,
        sealogEventOptionValues?: Array<string>,
        recordModifiedBefore?: string,
        recordModifiedAfter?: string,
        assetModifiedBefore?: string,
        assetModifiedAfter?: string,
        sortField?: 'asset_updated_dt' | 'record_updated_dt' | 'path',
        sortOrder?: 'asc' | 'desc',
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DataAssetWithManifestAndRealAssetDeploymentResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/search',
            query: {
                'query': query,
                'sealog_event_query': sealogEventQuery,
                'exclude': exclude,
                'regex': regex,
                'file_extensions': fileExtensions,
                'directory': directory,
                'parent_prefix': parentPrefix,
                'include_descendant_deployments': includeDescendantDeployments,
                'squash_by_path': squashByPath,
                'data_set_public_ids': dataSetPublicIds,
                'data_asset_public_ids': dataAssetPublicIds,
                'manifest_types': manifestTypes,
                'manifest_public_ids': manifestPublicIds,
                'real_asset_public_ids': realAssetPublicIds,
                'real_asset_deployment_public_ids': realAssetDeploymentPublicIds,
                'excluded_real_asset_deployment_public_ids': excludedRealAssetDeploymentPublicIds,
                'storage_tiers': storageTiers,
                'storage_tier_statuses': storageTierStatuses,
                'sealog_event_values': sealogEventValues,
                'sealog_event_free_texts': sealogEventFreeTexts,
                'sealog_event_option_names': sealogEventOptionNames,
                'sealog_event_option_values': sealogEventOptionValues,
                'record_modified_before': recordModifiedBefore,
                'record_modified_after': recordModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'asset_modified_after': assetModifiedAfter,
                'sort_field': sortField,
                'sort_order': sortOrder,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Search Statistics
     * @returns DataAssetSearchStatisticsResponse Successful Response
     * @throws ApiError
     */
    public getSearchStatistics({
        query,
        sealogEventQuery,
        exclude,
        regex = false,
        fileExtensions,
        directory,
        parentPrefix,
        includeDescendantDeployments = false,
        squashByPath = false,
        dataSetPublicIds,
        dataAssetPublicIds,
        manifestTypes,
        manifestPublicIds,
        realAssetPublicIds,
        realAssetDeploymentPublicIds,
        excludedRealAssetDeploymentPublicIds,
        storageTiers,
        storageTierStatuses,
        sealogEventValues,
        sealogEventFreeTexts,
        sealogEventOptionNames,
        sealogEventOptionValues,
        recordModifiedBefore,
        recordModifiedAfter,
        assetModifiedBefore,
        assetModifiedAfter,
        sortField = 'asset_updated_dt',
        sortOrder = 'desc',
    }: {
        query?: string,
        sealogEventQuery?: string,
        exclude?: string,
        regex?: boolean,
        fileExtensions?: Array<string>,
        directory?: string,
        parentPrefix?: string,
        includeDescendantDeployments?: boolean,
        squashByPath?: boolean,
        dataSetPublicIds?: Array<string>,
        dataAssetPublicIds?: Array<string>,
        manifestTypes?: Array<ManifestType>,
        manifestPublicIds?: Array<string>,
        realAssetPublicIds?: Array<string>,
        realAssetDeploymentPublicIds?: Array<string>,
        excludedRealAssetDeploymentPublicIds?: Array<string>,
        storageTiers?: Array<StorageTier>,
        storageTierStatuses?: Array<StorageTierStatus>,
        sealogEventValues?: Array<string>,
        sealogEventFreeTexts?: Array<string>,
        sealogEventOptionNames?: Array<string>,
        sealogEventOptionValues?: Array<string>,
        recordModifiedBefore?: string,
        recordModifiedAfter?: string,
        assetModifiedBefore?: string,
        assetModifiedAfter?: string,
        sortField?: 'asset_updated_dt' | 'record_updated_dt' | 'path',
        sortOrder?: 'asc' | 'desc',
    }): CancelablePromise<DataAssetSearchStatisticsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/search/statistics',
            query: {
                'query': query,
                'sealog_event_query': sealogEventQuery,
                'exclude': exclude,
                'regex': regex,
                'file_extensions': fileExtensions,
                'directory': directory,
                'parent_prefix': parentPrefix,
                'include_descendant_deployments': includeDescendantDeployments,
                'squash_by_path': squashByPath,
                'data_set_public_ids': dataSetPublicIds,
                'data_asset_public_ids': dataAssetPublicIds,
                'manifest_types': manifestTypes,
                'manifest_public_ids': manifestPublicIds,
                'real_asset_public_ids': realAssetPublicIds,
                'real_asset_deployment_public_ids': realAssetDeploymentPublicIds,
                'excluded_real_asset_deployment_public_ids': excludedRealAssetDeploymentPublicIds,
                'storage_tiers': storageTiers,
                'storage_tier_statuses': storageTierStatuses,
                'sealog_event_values': sealogEventValues,
                'sealog_event_free_texts': sealogEventFreeTexts,
                'sealog_event_option_names': sealogEventOptionNames,
                'sealog_event_option_values': sealogEventOptionValues,
                'record_modified_before': recordModifiedBefore,
                'record_modified_after': recordModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'asset_modified_after': assetModifiedAfter,
                'sort_field': sortField,
                'sort_order': sortOrder,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Tree Directories
     * @returns SQLModelPage_DataAssetTreeDirectoryResponse_ Successful Response
     * @throws ApiError
     */
    public listTreeDirectories({
        realAssetDeploymentPublicId,
        manifestTypes,
        directory,
        searchTerm,
        recursive = false,
        assetModifiedAfter,
        assetModifiedBefore,
        page = 1,
        size = 50,
    }: {
        realAssetDeploymentPublicId: string,
        manifestTypes?: Array<ManifestType>,
        directory?: string,
        searchTerm?: string,
        recursive?: boolean,
        assetModifiedAfter?: string,
        assetModifiedBefore?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DataAssetTreeDirectoryResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_asset/tree/{real_asset_deployment_public_id}/directories',
            path: {
                'real_asset_deployment_public_id': realAssetDeploymentPublicId,
            },
            query: {
                'manifest_types': manifestTypes,
                'directory': directory,
                'search_term': searchTerm,
                'recursive': recursive,
                'asset_modified_after': assetModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Data Sets
     * @returns SQLModelPage_DataSetResponse_ List data sets
     * @throws ApiError
     */
    public listDataSets({
        query,
        page = 1,
        size = 50,
    }: {
        query?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DataSetResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_set',
            query: {
                'query': query,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Data Set
     * @returns DataSetResponse Register a new data set
     * @throws ApiError
     */
    public createDataSet({
        requestBody,
    }: {
        requestBody: DataSetCreateRequest,
    }): CancelablePromise<DataSetResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data_set',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Data Set Detail
     * Detail for a data set
     * @returns DataSetDetailResponse Successful Response
     * @throws ApiError
     */
    public dataSetDetail({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<DataSetDetailResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_set/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Data Set
     * @returns any Delete a data set
     * @throws ApiError
     */
    public deleteDataSet({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/data_set/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Asset
     * @returns any Add a data asset to a data set
     * @throws ApiError
     */
    public addAsset({
        dataSetPublicId,
        dataAssetPublicId,
    }: {
        dataSetPublicId: string,
        dataAssetPublicId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/data_set/{data_set_public_id}/data_asset/{data_asset_public_id}',
            path: {
                'data_set_public_id': dataSetPublicId,
                'data_asset_public_id': dataAssetPublicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove Asset
     * @returns any Remove a data asset from a data set
     * @throws ApiError
     */
    public removeAsset({
        dataSetPublicId,
        dataAssetPublicId,
    }: {
        dataSetPublicId: string,
        dataAssetPublicId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/data_set/{data_set_public_id}/data_asset/{data_asset_public_id}',
            path: {
                'data_set_public_id': dataSetPublicId,
                'data_asset_public_id': dataAssetPublicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Add Assets By Id
     * @returns any Bulk add data assets to a data set
     * @throws ApiError
     */
    public bulkAddAssetsById({
        dataSetPublicId,
        requestBody,
    }: {
        dataSetPublicId: string,
        requestBody: Array<string>,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/data_set/{data_set_public_id}/data_asset/',
            path: {
                'data_set_public_id': dataSetPublicId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Add Assets From Search
     * Bulk add data assets matching the given search criteria into the data set
     * @returns DataSetDetailResponse Successful Response
     * @throws ApiError
     */
    public bulkAddAssetsFromSearch({
        dataSetPublicId,
        requestBody,
    }: {
        dataSetPublicId: string,
        requestBody: DataAssetSearchParameters,
    }): CancelablePromise<DataSetDetailResponse> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/data_set/{data_set_public_id}/bulk_add_from_search',
            path: {
                'data_set_public_id': dataSetPublicId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Download Export From Cloud
     * @returns void
     * @throws ApiError
     */
    public downloadExportFromCloud({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data_set/{public_id}/download',
            path: {
                'public_id': publicId,
            },
            errors: {
                307: `Download data set as a ZIP file`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Share Data Set
     * @returns SharedLinkResponse Generate sharing links for data set downloads
     * @throws ApiError
     */
    public shareDataSet({
        publicId,
        lifetimeS = 3600,
    }: {
        publicId: string,
        lifetimeS?: number,
    }): CancelablePromise<SharedLinkResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data_set/{public_id}/share',
            path: {
                'public_id': publicId,
            },
            query: {
                'lifetime_s': lifetimeS,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Directory Definitions
     * @returns SQLModelPage_DirectoryResponse_ Successful Response
     * @throws ApiError
     */
    public listDirectoryDefinitions({
        query,
        allowAddendumUpload,
        page = 1,
        size = 50,
    }: {
        query?: string,
        allowAddendumUpload?: boolean,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DirectoryResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/directory',
            query: {
                'query': query,
                'allow_addendum_upload': allowAddendumUpload,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Directory Definition
     * @returns DirectoryResponse Successful Response
     * @throws ApiError
     */
    public createDirectoryDefinition({
        requestBody,
    }: {
        requestBody: DirectoryCreateRequest,
    }): CancelablePromise<DirectoryResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/directory',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Directory Data Experts
     * @returns DirectoryDataExpertUserResponse Successful Response
     * @throws ApiError
     */
    public listDirectoryDataExperts(): CancelablePromise<Array<DirectoryDataExpertUserResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/directory/data_experts',
        });
    }

    /**
     * Get Directory Inventory Summary
     * @returns DirectoryInventorySummaryResponse Successful Response
     * @throws ApiError
     */
    public getDirectoryInventorySummary(): CancelablePromise<DirectoryInventorySummaryResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/directory/inventory/summary',
        });
    }

    /**
     * List Directory Inventory
     * @returns SQLModelPage_DirectoryInventoryListItem_ Successful Response
     * @throws ApiError
     */
    public listDirectoryInventory({
        realAssetDeploymentPublicId,
        manifestTypes,
        curated,
        query,
        groupBy,
        page = 1,
        size = 50,
    }: {
        realAssetDeploymentPublicId?: string,
        manifestTypes?: Array<ManifestType>,
        curated?: boolean,
        query?: string,
        groupBy?: DirectoryInventoryGroupBy,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_DirectoryInventoryListItem_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/directory/inventory',
            query: {
                'real_asset_deployment_public_id': realAssetDeploymentPublicId,
                'manifest_types': manifestTypes,
                'curated': curated,
                'query': query,
                'group_by': groupBy,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Preview Directory Definition
     * @returns DirectoryPreviewResponse Successful Response
     * @throws ApiError
     */
    public previewDirectoryDefinition({
        requestBody,
    }: {
        requestBody: DirectoryPreviewRequest,
    }): CancelablePromise<DirectoryPreviewResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/directory/preview',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Directory Definition
     * @returns DirectoryResponse Successful Response
     * @throws ApiError
     */
    public getDirectoryDefinition({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<DirectoryResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/directory/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove Directory Definition
     * @returns void
     * @throws ApiError
     */
    public removeDirectoryDefinition({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/directory/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Patch Directory Definition
     * @returns DirectoryResponse Successful Response
     * @throws ApiError
     */
    public patchDirectoryDefinition({
        publicId,
        requestBody,
    }: {
        publicId: string,
        requestBody: DirectoryUpdateRequest,
    }): CancelablePromise<DirectoryResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/directory/{public_id}',
            path: {
                'public_id': publicId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Real Assets
     * @returns SQLModelPage_RealAssetResponse_ Successful Response
     * @throws ApiError
     */
    public listRealAssets({
        query,
        type,
        exclude,
        page = 1,
        size = 50,
    }: {
        query?: string,
        type?: Array<RealAssetType>,
        exclude?: Array<RealAssetType>,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_RealAssetResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/',
            query: {
                'query': query,
                'type': type,
                'exclude': exclude,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Real Asset
     * @returns RealAssetResponse Successful Response
     * @throws ApiError
     */
    public createRealAsset({
        requestBody,
    }: {
        requestBody: RealAssetCreateRequest,
    }): CancelablePromise<RealAssetResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/real_asset/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Real Asset Detail
     * @returns RealAssetWithDeploymentsResponse Successful Response
     * @throws ApiError
     */
    public realAssetDetail({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<RealAssetWithDeploymentsResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/{public_id}',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Cruise Real Asset Deployment
     * @returns RealAssetDeploymentWithRealAssetResponse Successful Response
     * @throws ApiError
     */
    public createCruiseRealAssetDeployment({
        requestBody,
    }: {
        requestBody: CruiseRealAssetDeploymentCreateRequest,
    }): CancelablePromise<RealAssetDeploymentWithRealAssetResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/real_asset/deployment/cruise',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Real Asset Tree
     * @returns RealAssetTreeResponse Successful Response
     * @throws ApiError
     */
    public listRealAssetTree(): CancelablePromise<RealAssetTreeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/tree/',
        });
    }

    /**
     * List Real Asset Deployments
     * @returns SQLModelPage_RealAssetDeploymentResponse_ Successful Response
     * @throws ApiError
     */
    public listRealAssetDeployments({
        query,
        externalId,
        parentDeploymentExternalId,
        realAssetType,
        realAssetPublicId,
        deploymentStartBeforeDt,
        deploymentStartAfterDt,
        page = 1,
        size = 50,
    }: {
        query?: string,
        externalId?: Array<string>,
        parentDeploymentExternalId?: Array<string>,
        realAssetType?: RealAssetType,
        realAssetPublicId?: Array<string>,
        deploymentStartBeforeDt?: string,
        deploymentStartAfterDt?: string,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_RealAssetDeploymentResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/deployment/',
            query: {
                'query': query,
                'external_id': externalId,
                'parent_deployment_external_id': parentDeploymentExternalId,
                'real_asset_type': realAssetType,
                'real_asset_public_id': realAssetPublicId,
                'deployment_start_before_dt': deploymentStartBeforeDt,
                'deployment_start_after_dt': deploymentStartAfterDt,
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Real Asset Deployment Detail
     * @returns RealAssetDeploymentWithChildrenResponse Successful Response
     * @throws ApiError
     */
    public realAssetDeploymentDetail({
        publicId,
    }: {
        publicId: string,
    }): CancelablePromise<RealAssetDeploymentWithChildrenResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/deployment/{public_id}/',
            path: {
                'public_id': publicId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Real Asset Deployment Tree
     * @returns DeploymentTreeResponse Successful Response
     * @throws ApiError
     */
    public listRealAssetDeploymentTree(): CancelablePromise<DeploymentTreeResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/deployment/tree',
        });
    }

    /**
     * List Related Deployments
     * Get related deployments for a given deployment ID.
     *
     * Returns deployments with their relationship types (parent, child, sibling, uncle, cousin).
     * Supports filtering by date range and ancestor deployment.
     * @returns RelatedDeploymentResponse Successful Response
     * @throws ApiError
     */
    public listRelatedDeployments({
        publicId,
        minDeploymentStartDate,
        maxDeploymentEndDate,
        nearbyDistanceToleranceMeters = 100000,
    }: {
        publicId: string,
        minDeploymentStartDate?: string,
        maxDeploymentEndDate?: string,
        nearbyDistanceToleranceMeters?: number,
    }): CancelablePromise<Array<RelatedDeploymentResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/real_asset/deployment/{public_id}/related',
            path: {
                'public_id': publicId,
            },
            query: {
                'min_deployment_start_date': minDeploymentStartDate,
                'max_deployment_end_date': maxDeploymentEndDate,
                'nearby_distance_tolerance_meters': nearbyDistanceToleranceMeters,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Link Data Assets
     * @returns any Successful Response
     * @throws ApiError
     */
    public linkDataAssets({
        publicId,
        requestBody,
    }: {
        publicId: string,
        requestBody: DataAssetSearchParameters,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/real_asset/deployment/{public_id}/link_data_assets',
            path: {
                'public_id': publicId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Unlink Data Assets
     * @returns any Successful Response
     * @throws ApiError
     */
    public unlinkDataAssets({
        publicId,
        query,
        sealogEventQuery,
        exclude,
        regex = false,
        fileExtensions,
        directory,
        parentPrefix,
        includeDescendantDeployments = false,
        squashByPath = false,
        dataSetPublicIds,
        dataAssetPublicIds,
        manifestTypes,
        manifestPublicIds,
        realAssetPublicIds,
        realAssetDeploymentPublicIds,
        excludedRealAssetDeploymentPublicIds,
        storageTiers,
        storageTierStatuses,
        sealogEventValues,
        sealogEventFreeTexts,
        sealogEventOptionNames,
        sealogEventOptionValues,
        recordModifiedBefore,
        recordModifiedAfter,
        assetModifiedBefore,
        assetModifiedAfter,
        sortField = 'asset_updated_dt',
        sortOrder = 'desc',
    }: {
        publicId: string,
        query?: string,
        sealogEventQuery?: string,
        exclude?: string,
        regex?: boolean,
        fileExtensions?: Array<string>,
        directory?: string,
        parentPrefix?: string,
        includeDescendantDeployments?: boolean,
        squashByPath?: boolean,
        dataSetPublicIds?: Array<string>,
        dataAssetPublicIds?: Array<string>,
        manifestTypes?: Array<ManifestType>,
        manifestPublicIds?: Array<string>,
        realAssetPublicIds?: Array<string>,
        realAssetDeploymentPublicIds?: Array<string>,
        excludedRealAssetDeploymentPublicIds?: Array<string>,
        storageTiers?: Array<StorageTier>,
        storageTierStatuses?: Array<StorageTierStatus>,
        sealogEventValues?: Array<string>,
        sealogEventFreeTexts?: Array<string>,
        sealogEventOptionNames?: Array<string>,
        sealogEventOptionValues?: Array<string>,
        recordModifiedBefore?: string,
        recordModifiedAfter?: string,
        assetModifiedBefore?: string,
        assetModifiedAfter?: string,
        sortField?: 'asset_updated_dt' | 'record_updated_dt' | 'path',
        sortOrder?: 'asc' | 'desc',
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/real_asset/deployment/{public_id}/unlink_data_assets',
            path: {
                'public_id': publicId,
            },
            query: {
                'query': query,
                'sealog_event_query': sealogEventQuery,
                'exclude': exclude,
                'regex': regex,
                'file_extensions': fileExtensions,
                'directory': directory,
                'parent_prefix': parentPrefix,
                'include_descendant_deployments': includeDescendantDeployments,
                'squash_by_path': squashByPath,
                'data_set_public_ids': dataSetPublicIds,
                'data_asset_public_ids': dataAssetPublicIds,
                'manifest_types': manifestTypes,
                'manifest_public_ids': manifestPublicIds,
                'real_asset_public_ids': realAssetPublicIds,
                'real_asset_deployment_public_ids': realAssetDeploymentPublicIds,
                'excluded_real_asset_deployment_public_ids': excludedRealAssetDeploymentPublicIds,
                'storage_tiers': storageTiers,
                'storage_tier_statuses': storageTierStatuses,
                'sealog_event_values': sealogEventValues,
                'sealog_event_free_texts': sealogEventFreeTexts,
                'sealog_event_option_names': sealogEventOptionNames,
                'sealog_event_option_values': sealogEventOptionValues,
                'record_modified_before': recordModifiedBefore,
                'record_modified_after': recordModifiedAfter,
                'asset_modified_before': assetModifiedBefore,
                'asset_modified_after': assetModifiedAfter,
                'sort_field': sortField,
                'sort_order': sortOrder,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
